import { useState } from 'react'
import { Cloud, KeyRound, LogIn, ShieldCheck } from 'lucide-react'
import { allowedEmail, firebaseConfigured } from '../lib/firebase'
import {
  completeTotpSignIn,
  signInWithGoogle,
  signInWithPassword,
  type SignInResult,
  type TotpChallenge,
} from '../lib/cloud'

function authErrorMessage(reason: unknown): string {
  const code =
    typeof reason === 'object' && reason !== null && 'code' in reason
      ? String(reason.code)
      : ''

  switch (code) {
    case 'auth/invalid-verification-code':
      return 'Неверный код. Дождитесь нового кода и повторите.'
    case 'auth/code-expired':
      return 'Код устарел. Введите новый код из Authenticator.'
    case 'auth/invalid-multi-factor-session':
    case 'auth/missing-multi-factor-session':
    case 'auth/multi-factor-info-not-found':
      return 'Сеанс подтверждения истёк. Начните вход заново.'
    case 'auth/network-request-failed':
      return 'Нет связи с сервером. Проверьте интернет и повторите.'
    case 'auth/too-many-requests':
      return 'Слишком много попыток. Подождите и повторите позже.'
    default:
      return reason instanceof Error ? reason.message : 'Не удалось подтвердить вход.'
  }
}

export function AuthScreen() {
  const [loading, setLoading] = useState(false)
  const [passwordMode, setPasswordMode] = useState(false)
  const [password, setPassword] = useState('')
  const [challenge, setChallenge] = useState<TotpChallenge | null>(null)
  const [factorUid, setFactorUid] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState('')

  function handlePrimaryResult(result: SignInResult) {
    if (result.kind === 'totp-required') {
      setPassword('')
      setChallenge(result.challenge)
      setFactorUid(result.challenge.factors[0]?.uid ?? '')
      setTotpCode('')
    }
  }

  async function signInGoogle() {
    setLoading(true)
    setError('')
    try {
      handlePrimaryResult(await signInWithGoogle())
    } catch (reason) {
      setError(authErrorMessage(reason))
    } finally {
      setLoading(false)
    }
  }

  async function signInPassword(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setLoading(true)
    setError('')
    try {
      handlePrimaryResult(await signInWithPassword(password))
    } catch (reason) {
      setError(authErrorMessage(reason))
    } finally {
      setLoading(false)
    }
  }

  async function verifyTotp(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!challenge) return

    setLoading(true)
    setError('')
    try {
      await completeTotpSignIn(challenge, factorUid, totpCode)
      setChallenge(null)
      setTotpCode('')
    } catch (reason) {
      setError(authErrorMessage(reason))
    } finally {
      setLoading(false)
    }
  }

  function restartSignIn() {
    setChallenge(null)
    setFactorUid('')
    setTotpCode('')
    setPassword('')
    setError('')
  }

  return (
    <main className="auth-screen">
      <section className="auth-panel">
        <div className="logo-mark">WS</div>
        <div>
          <span className="eyebrow">Защищённое рабочее пространство</span>
          <h1>{challenge ? 'Подтвердите вход' : 'WorkSmarter'}</h1>
          <p>
            {challenge
              ? 'Введите код из приложения Authenticator.'
              : 'Клиентская база синхронизируется между доверенными устройствами.'}
          </p>
        </div>

        <div className="auth-features">
          <span><ShieldCheck size={18} /> Доступ только для владельца</span>
          <span><Cloud size={18} /> Облачное хранение с локальным кешем</span>
        </div>

        {firebaseConfigured ? (
          challenge ? (
            <form className="auth-password-form" onSubmit={(event) => void verifyTotp(event)}>
              {challenge.factors.length > 1 ? (
                <label>
                  <span>Authenticator</span>
                  <select value={factorUid} onChange={(event) => setFactorUid(event.target.value)}>
                    {challenge.factors.map((factor) => (
                      <option key={factor.uid} value={factor.uid}>
                        {factor.displayName || 'WorkSmarter Authenticator'}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label>
                <span>Шестизначный код</span>
                <input
                  autoFocus
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
              </label>
              <button className="button primary full-width auth-button" type="submit" disabled={loading || totpCode.length !== 6}>
                <ShieldCheck size={18} />
                {loading ? 'Проверяю…' : 'Подтвердить вход'}
              </button>
              <button className="text-button auth-mode-button" type="button" disabled={loading} onClick={restartSignIn}>
                Начать вход заново
              </button>
            </form>
          ) : (
            <>
              {passwordMode ? (
                <form className="auth-password-form" onSubmit={(event) => void signInPassword(event)}>
                  <label>
                    <span>Аккаунт</span>
                    <input value={allowedEmail} disabled />
                  </label>
                  <label>
                    <span>Пароль</span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                    />
                  </label>
                  <button className="button primary full-width auth-button" type="submit" disabled={loading || !password}>
                    <KeyRound size={18} />
                    {loading ? 'Проверяю…' : 'Войти по паролю'}
                  </button>
                </form>
              ) : (
                <button className="button primary full-width auth-button" type="button" onClick={() => void signInGoogle()} disabled={loading}>
                  <LogIn size={18} />
                  {loading ? 'Открываю вход…' : 'Войти через Google'}
                </button>
              )}

              <button
                className="text-button auth-mode-button"
                type="button"
                disabled={loading}
                onClick={() => {
                  setPasswordMode((current) => !current)
                  setError('')
                }}
              >
                {passwordMode ? 'Вернуться ко входу через Google' : 'Войти по почте и паролю'}
              </button>
            </>
          )
        ) : (
          <p className="form-error">Firebase не настроен для production-сборки.</p>
        )}

        <small>Разрешённый аккаунт: {allowedEmail}</small>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
      </section>
    </main>
  )
}
